import { Col, Form, Input, message, Row } from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { EBButtonV2 } from "../components/common/EBButtonV2";
import { EBModalV2 } from "../components/common/EBModalV2";
import { ListItemLayoutDefault } from "../list/ListItem.Layout";
import { ListItemProps, ListV2 } from "../list/ListV2";
import { SearchRequest } from "../model/search/UserSearchRequestV1";
import { PageResponse } from "../paging/Paging.Model";

const API_URL = "/api/system/parameters";

interface SystemParameter {
  id: number;
  name: string;
  value: string;
}

export const SystemParametersPageContent: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [refreshKey, setRefreshKey] = useState(0);
  const [editedValues, setEditedValues] = useState<Record<number, string>>({});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const modalRef = useRef<any>(null);

  useEffect(() => {
    const modalEl = document.getElementById("system-parameter-edit-modal");
    if (!modalEl) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modalRef.current = new (window as any).bootstrap.Modal(modalEl);

    const handleHide = () => {
      setIsModalVisible(false);
    };
    modalEl.addEventListener("hidden.bs.modal", handleHide);

    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handleHide);
    };
  }, []);

  useEffect(() => {
    if (!modalRef.current) return;
    if (isModalVisible) {
      modalRef.current.show();
    } else {
      modalRef.current.hide();
    }
  }, [isModalVisible]);

  const onFetchParameters = async (
    searchRequest?: SearchRequest
  ): Promise<PageResponse<SystemParameter>> => {
    try {
      const response = await axios.get<SystemParameter[]>(API_URL);
      const allParameters = response.data;

      const page = searchRequest?.pageParams?.page ?? 0;
      const size = searchRequest?.pageParams?.size ?? 10;

      const paginatedParameters = allParameters.slice(
        page * size,
        (page + 1) * size
      );

      return {
        content: paginatedParameters,
        totalSize: allParameters.length,
      } as PageResponse<SystemParameter>;
    } catch (error) {
      message.error("Failed to fetch system parameters");
      return { content: [], totalSize: 0 } as PageResponse<SystemParameter>;
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await axios.post(API_URL, values);
      message.success("Parameter created successfully");
      setRefreshKey((k) => k + 1);
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to save parameter");
    }
  };

  const handleValueChange = (id: number, value: string) => {
    setEditedValues((prev) => ({ ...prev, [id]: value }));
  };

  const handleInlineSave = async (item: SystemParameter) => {
    const newValue = editedValues[item.id];
    if (newValue === undefined) {
      return;
    }

    try {
      await axios.put(`${API_URL}/${item.id}`, { name: item.name, value: newValue });
      message.success("Parameter updated successfully");
      setEditedValues((prev) => {
        const newValues = { ...prev };
        delete newValues[item.id];
        return newValues;
      });
      setRefreshKey((k) => k + 1);
    } catch (error) {
      message.error("Failed to save parameter");
    }
  };

  const renderParameterItem = (itemProps: ListItemProps) => {
    const item = itemProps.item as SystemParameter;
    const isEdited = editedValues[item.id] !== undefined;
    const currentValue = editedValues[item.id] ?? item.value;

    return (
      <Row align="middle" gutter={16}>
        <Col xs={24} sm={2} className="fw-bold">
          #{item.id}
        </Col>
        <Col xs={24} sm={6}>
          {item.name}
        </Col>
        <Col xs={24} sm={10}>
          <Input.TextArea
            value={currentValue}
            onChange={(e) => handleValueChange(item.id, e.target.value)}
            autoSize={{ minRows: 1, maxRows: 5 }}
          />
        </Col>
        <Col xs={24} sm={6} className="text-end">
          <EBButtonV2
            purpose="primary"
            onClick={() => handleInlineSave(item)}
            content={{ normal: "Save" }}
            disabled={!isEdited}
          />
        </Col>
      </Row>
    );
  };

  return (
    <div>
      <EBButtonV2
        purpose="primary"
        onClick={() => {
          form.resetFields();
          setIsModalVisible(true);
        }}
        content={{ normal: "Create Parameter" }}
        rowClassName="mb-3"
      />
      <div className="card">
        <div className="card-body">
          <Row
            align="middle"
            className="pb-3 border-bottom mb-3 fw-bold d-none d-sm-flex"
          >
            <Col sm={2}>ID</Col>
            <Col sm={6}>Name</Col>
            <Col sm={10}>Value</Col>
            <Col sm={6} className="text-end">
              Action
            </Col>
          </Row>
          <ListV2
            key={refreshKey}
            onFetch={onFetchParameters}
            itemNode={renderParameterItem}
            ItemLayoutComponent={ListItemLayoutDefault}
          />
        </div>
      </div>
      <EBModalV2
        id="system-parameter-edit-modal"
        header="Create Parameter"
        body={
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="value" label="Value" rules={[{ required: true }]}>
              <Input.TextArea autoSize={{ minRows: 4, maxRows: 10 }} />
            </Form.Item>
          </Form>
        }
        primaryButtonProps={{
          content: { normal: "Save" },
          onClick: handleCreate,
        }}
      />
    </div>
  );
};
